<template>
  <div class="cm-root" :data-open="String(isOpen)">
    <div class="cm-overlay" part="overlay" @click="close"></div>
    <div
      ref="dialogWrapperRef"
      class="cm-dialog-wrapper cm-dialog-wrapper--expert"
      :class="{ 'cm-shake': isShaking }"
    >
      <div class="cm-dialog cm-dialog--expert" part="dialog" role="dialog" aria-modal="true">
        <div class="cm-header">
          <div class="cm-brand">
            <div v-if="state.optsRef.value.brandName" class="cm-brand-title">{{ brandTitle }}</div>
            <div>
              <div class="cm-title">{{ headerTitle }}</div>
              <div class="cm-subtitle">{{ headerSubtitle }}</div>
            </div>
          </div>
          <div class="cm-header-actions">
            <LanguageSelector />
            <button class="cm-close" type="button" aria-label="Close" @click="close">✕</button>
          </div>
        </div>

        <div ref="bodyRef" class="cm-body" :class="{ 'cm-body--success': step === 2 }">
          <div class="cm-step" :data-active="String(step === 1)">
            <form ref="formRootRef" class="cm-form cm-form-expert" @submit.prevent>
              <div class="cm-expert-grid">
                <div class="cm-field" :class="{ 'cm-invalid': !!errors.firstName }">
                  <label class="cm-label" for="cm-firstName"
                    >{{ t('contactForm.firstName') }} {{ t('contactForm.requiredMark') }}</label
                  >
                  <input
                    id="cm-firstName"
                    class="cm-input"
                    name="first_name"
                    type="text"
                    autocomplete="given-name"
                    :placeholder="t('contactForm.firstNamePlaceholder')"
                    :value="state.form.value.firstName"
                    @focus="onFieldFirstInteract('first_name')"
                    @input="
                      state.updateForm({
                        firstName: ($event.target as HTMLInputElement).value,
                      })
                    "
                  />
                  <div class="cm-error">{{ errors.firstName }}</div>
                </div>

                <div class="cm-field" :class="{ 'cm-invalid': !!errors.email }">
                  <label class="cm-label" for="cm-email"
                    >{{ t('contactForm.email') }} {{ t('contactForm.requiredMark') }}</label
                  >
                  <div class="cm-input-wrap">
                    <span class="cm-input-icon" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M4 6h16v12H4V6zm0 0l8 6 8-6"
                          stroke="currentColor"
                          stroke-width="1.6"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    <input
                      id="cm-email"
                      class="cm-input cm-input--with-icon"
                      name="email"
                      type="email"
                      autocomplete="email"
                      :placeholder="t('contactForm.emailPlaceholder')"
                      :value="state.form.value.email"
                      @focus="onFieldFirstInteract('email')"
                      @input="
                        state.updateForm({ email: ($event.target as HTMLInputElement).value })
                      "
                    />
                  </div>
                  <div class="cm-error">{{ errors.email }}</div>
                </div>
              </div>

              <div class="cm-field" :class="{ 'cm-invalid': !!errors.phone }">
                <label class="cm-label" for="cm-phone"
                  >{{ t('contactForm.phone') }} / WhatsApp {{ t('contactForm.requiredMark') }}</label
                >
                <div class="cm-input-wrap">
                  <span class="cm-input-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6.5 3h3l1.5 4-2 1.5c1.2 3 3.8 5.6 6.8 6.8L17 14l4 1.5v3a2 2 0 0 1-2.2 2C9.6 19.4 4.6 14.4 3 6.7A2 2 0 0 1 5 3.5"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <input
                    id="cm-phone"
                    class="cm-input cm-input--with-icon"
                    name="phone"
                    type="tel"
                    autocomplete="tel"
                    :placeholder="t('contactForm.phonePlaceholder')"
                    :value="state.form.value.phone"
                    @focus="onFieldFirstInteract('phone')"
                    @blur="normalizePhoneField"
                    @input="
                      state.updateForm({ phone: ($event.target as HTMLInputElement).value })
                    "
                  />
                </div>
                <label class="cm-check cm-check--tight">
                  <input
                    type="checkbox"
                    name="whatsapp_available"
                    :checked="state.form.value.hasWhatsApp"
                    @change="onHasWhatsAppChange"
                  />
                  <span>{{ t('contactForm.availableOnWhatsapp') }}</span>
                </label>
                <div class="cm-error">{{ errors.phone }}</div>
              </div>

              <div class="cm-expert-grid cm-expert-grid--radios">
                <div class="cm-field" :class="{ 'cm-invalid': !!errors.budget }">
                  <div class="cm-label"
                    >{{ t('contactForm.budget') }} {{ t('contactForm.requiredMark') }}</div
                  >
                  <div class="cm-radio-list" role="radiogroup" :aria-label="t('contactForm.budget')">
                    <label
                      v-for="opt in budgetOptions"
                      :key="opt.uiKey"
                      class="cm-radio-row"
                      :class="{
                        'cm-radio-row--selected': selectedBudgetUiKey === opt.uiKey,
                      }"
                    >
                      <input
                        type="radio"
                        name="budget"
                        :value="opt.uiKey"
                        :checked="selectedBudgetUiKey === opt.uiKey"
                        @change="onBudgetOptionChange(opt)"
                      />
                      <span class="cm-radio-row-label">{{ t(opt.labelKey) }}</span>
                    </label>
                  </div>
                  <div class="cm-error">{{ errors.budget }}</div>
                </div>

                <div class="cm-field" :class="{ 'cm-invalid': !!errors.timeline }">
                  <div class="cm-label"
                    >{{ t('contactForm.buyTiming') }} {{ t('contactForm.requiredMark') }}</div
                  >
                  <div
                    class="cm-radio-list"
                    role="radiogroup"
                    :aria-label="t('contactForm.buyTiming')"
                  >
                    <label
                      v-for="opt in timelineOptions"
                      :key="opt.uiKey"
                      class="cm-radio-row"
                      :class="{
                        'cm-radio-row--selected': selectedTimelineUiKey === opt.uiKey,
                      }"
                    >
                      <input
                        type="radio"
                        name="timeline"
                        :value="opt.uiKey"
                        :checked="selectedTimelineUiKey === opt.uiKey"
                        @change="onBuyTimingOptionChange(opt)"
                      />
                      <span class="cm-radio-row-label">{{ t(opt.labelKey) }}</span>
                    </label>
                  </div>
                  <div class="cm-error">{{ errors.timeline }}</div>
                </div>
              </div>

              <p class="cm-legal" v-html="t('contactForm.footerPrivacy')"></p>
            </form>
          </div>

          <div class="cm-step" :data-active="String(step === 2)">
            <SuccessStep />
          </div>
        </div>

        <div class="cm-footer" part="footer">
          <button
            v-if="step === 1"
            class="cm-btn cm-btn-primary cm-btn-block"
            type="button"
            :disabled="isBusy"
            @click="primary"
          >
            {{ isBusy ? t('contactForm.sending') : t('contactForm.ctaMatching') }}
          </button>
          <button
            v-else
            class="cm-btn cm-btn-primary cm-btn-block"
            type="button"
            :disabled="isBusy"
            @click="close"
          >
            {{ t('contactForm.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import SuccessStep from './SuccessStep.vue'
  import LanguageSelector from './LanguageSelector.vue'
  import type { ContactMeOpenDetail, ContactMeSubmitPayload, ContactMeWidgetOptions } from '../types'
  import type { SupportedLocale } from '../types'
  import { normalizePhoneForSubmit } from '../utils/phone'
  import { normalizeWhitespace } from '../utils/validation'
  import { detectLocale } from '../i18n/detect'
  import {
    useContactFormState,
    useContactFormValidation,
    useContactFormSubmit,
    useContactMeEvents,
    useContactMeAnalytics,
  } from '../composables'
  import { LEAD_BUDGET_OPTIONS, LEAD_TIMELINE_OPTIONS } from '../constants/mauticForm4'

  const budgetOptions = LEAD_BUDGET_OPTIONS
  const timelineOptions = LEAD_TIMELINE_OPTIONS

  const { t, locale } = useI18n({ useScope: 'global' })
  const state = useContactFormState({ t })

  const isOpen = computed(() => state.isOpen.value)
  const step = computed(() => state.step.value)
  const isBusy = computed(() => state.isBusy.value)
  const isShaking = computed(() => state.isShaking.value)
  const errors = computed(() => state.errors.value)
  const brandTitle = computed(() => state.brandTitle.value)
  const headerTitle = computed(() => state.headerTitle.value)
  const headerSubtitle = computed(() => state.headerSubtitle.value)

  const events = useContactMeEvents()
  const analytics = useContactMeAnalytics()
  const bodyRef = ref<HTMLElement | null>(null)
  const selectedBudgetUiKey = ref('')
  const selectedTimelineUiKey = ref('')

  const fieldInteractOnce = new Set<string>()

  function syncSelectedOptionsFromForm() {
    selectedBudgetUiKey.value =
      budgetOptions.find(opt => opt.value === state.form.value.budget)?.uiKey ?? ''
    selectedTimelineUiKey.value =
      timelineOptions.find(opt => opt.value === state.form.value.timeline)?.uiKey ?? ''
  }

  function resetBodyScroll() {
    const body = bodyRef.value
    if (!body) return
    body.scrollTop = 0
    body.scrollLeft = 0
  }

  function normalizedPhoneValue() {
    return normalizePhoneForSubmit(state.form.value.phone, {
      locale: String(locale.value || ''),
      context: state.openDetail.value?.context,
    })
  }

  function normalizePhoneField() {
    const normalized = normalizedPhoneValue()
    if (normalized && normalized !== state.form.value.phone) {
      state.updateForm({ phone: normalized })
    }
  }

  function onFieldFirstInteract(fieldName: string) {
    if (fieldInteractOnce.has(fieldName)) return
    fieldInteractOnce.add(fieldName)
    analytics.trackFieldFirstInteraction(fieldName)
  }

  function onHasWhatsAppChange(e: Event) {
    onFieldFirstInteract('whatsapp_available')
    state.updateForm({ hasWhatsApp: (e.target as HTMLInputElement).checked })
  }

  function onBudgetOptionChange(option: (typeof budgetOptions)[number]) {
    onFieldFirstInteract('budget')
    selectedBudgetUiKey.value = option.uiKey
    state.updateForm({ budget: option.value })
  }

  function onBuyTimingOptionChange(option: (typeof timelineOptions)[number]) {
    onFieldFirstInteract('timeline')
    selectedTimelineUiKey.value = option.uiKey
    state.updateForm({ timeline: option.value })
  }

  const validation = useContactFormValidation(
    state.form,
    state.errors,
    t,
    state.setError,
    state.shake,
    locale
  )

  const { submit } = useContactFormSubmit(
    state.form,
    state.optsRef,
    state.openDetail,
    state.isBusy,
    state.setError,
    t,
    payload => events.emitSubmitted(payload),
    err => {
      const propertyId = getPropertyIdFromContext(state.openDetail.value?.context)
      events.emitError('submit', err, buildPayload())
      analytics.trackSubmitError({ propertyId, code: err })
      state.shake()
    }
  )

  const dialogWrapperRef = ref<HTMLElement | null>(null)
  const formRootRef = ref<HTMLElement | null>(null)

  function getPropertyIdFromContext(context?: ContactMeOpenDetail['context']): string | undefined {
    if (!context || typeof context !== 'object') return undefined
    const id =
      (context as Record<string, unknown>).propertyId ??
      (context as Record<string, unknown>).property_id
    return id != null ? String(id) : undefined
  }

  let autoCloseTimer: number | undefined
  function showSuccessAndAutoClose() {
    state.step.value = 2
    void nextTick(() => {
      resetBodyScroll()
    })
    if (autoCloseTimer) window.clearTimeout(autoCloseTimer)
    autoCloseTimer = window.setTimeout(() => {
      if (state.step.value === 2) {
        const wrapper = dialogWrapperRef.value
        if (wrapper) {
          wrapper.classList.add('cm-vanish')
          setTimeout(() => close(), 800)
        } else {
          close()
        }
      }
    }, 3000)
  }

  function buildPayload(): ContactMeSubmitPayload {
    return {
      firstName: normalizeWhitespace(state.form.value.firstName),
      email: state.form.value.email.trim(),
      phone: normalizedPhoneValue(),
      hasWhatsApp: state.form.value.hasWhatsApp,
      budget: state.form.value.budget,
      timeline: state.form.value.timeline,
      source: state.optsRef.value.source ?? 'website',
      context: state.openDetail.value?.context,
    }
  }

  function scrollToFirstInvalid() {
    nextTick(() => {
      const root = formRootRef.value
      const el = root?.querySelector<HTMLElement>('.cm-field.cm-invalid')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.querySelector<HTMLInputElement>('input')?.focus()
      }
    })
  }

  async function primary() {
    if (state.step.value !== 1) return
    normalizePhoneField()
    if (!validation.validate()) {
      scrollToFirstInvalid()
      return
    }
    const propertyId = getPropertyIdFromContext(state.openDetail.value?.context)
    const payload = buildPayload()
    events.emitSubmitAttempt(payload)
    analytics.trackSubmitAttempt(propertyId)
    await submit(buildPayload, conversationId => {
      analytics.trackStep2Submit(propertyId)
      analytics.trackConversion({ conversationId, propertyId })
      analytics.trackSubmitSuccess(propertyId)
      showSuccessAndAutoClose()
    })
  }

  function applyLocale() {
    const next: SupportedLocale =
      !state.optsRef.value.lang || state.optsRef.value.lang === 'auto'
        ? detectLocale()
        : (state.optsRef.value.lang as SupportedLocale)
    locale.value = next
  }

  async function open(detail?: ContactMeOpenDetail) {
    fieldInteractOnce.clear()
    dialogWrapperRef.value?.classList.remove('cm-vanish')
    applyLocale()
    analytics.init(state.optsRef.value.gtagId)
    state.openDetail.value = detail
    state.applyPrefill(detail?.prefill)
    syncSelectedOptionsFromForm()
    state.step.value = 1
    state.isOpen.value = true
    analytics.trackOpen(detail)
    events.emitOpened(detail)
    await nextTick()
    resetBodyScroll()
    document.getElementById('cm-firstName')?.focus()
  }

  function close() {
    if (!state.isOpen.value) return
    fieldInteractOnce.clear()
    selectedBudgetUiKey.value = ''
    selectedTimelineUiKey.value = ''
    dialogWrapperRef.value?.classList.remove('cm-vanish')
    state.isOpen.value = false
    state.reset()
    resetBodyScroll()
    events.emitClosed()
  }

  function configure(o: ContactMeWidgetOptions) {
    state.optsRef.value = { ...state.optsRef.value, ...o }
    applyLocale()
  }

  const onLocaleChange = (e: Event) => {
    const detail = (e as CustomEvent<{ locale: SupportedLocale | 'auto' }>).detail
    if (detail?.locale) {
      if (detail.locale === 'auto') {
        applyLocale()
      } else {
        locale.value = detail.locale
      }
    }
  }

  onMounted(() => {
    window.addEventListener('contactme:change-locale', onLocaleChange)
  })

  onUnmounted(() => {
    window.removeEventListener('contactme:change-locale', onLocaleChange)
  })

  defineExpose({ open, close, configure })

  onBeforeUnmount(() => {
    if (autoCloseTimer) window.clearTimeout(autoCloseTimer)
  })
</script>
